import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface MemberCoupon {
  id: string;
  campaignId: string;
  code: string;
  name: string;
  description: string | null;
  discountType: 'fixed' | 'percent';
  discountValue: number;
  minOrderAmount: number;
  issuedAt: string;
  expiresAt: string;
  usedAt: string | null;
  status: 'available' | 'used' | 'expired';
  useCount: number;
  maxUses: number;
  remainingUses: number;
}

export const useMemberCoupons = (availableOnly = false) => {
  const { member } = useAuth();

  return useQuery({
    queryKey: ['member-coupons', member?.lineUserId, availableOnly],
    queryFn: async (): Promise<MemberCoupon[]> => {
      if (!member?.lineUserId || !member?.lineAccessToken) {
        return [];
      }

      const { data, error } = await supabase.functions.invoke('get-member-coupons', {
        body: {
          availableOnly,
        },
        headers: {
          Authorization: `Bearer ${member.lineAccessToken}`,
        },
      });

      if (error) {
        console.error('[useMemberCoupons] Error:', error);
        throw error;
      }

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to fetch coupons');
      }

      return data.coupons || [];
    },
    enabled: !!member?.lineUserId && !!member?.lineAccessToken,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
  });
};

// 計算優惠券折扣金額
export const calculateDiscount = (
  coupon: MemberCoupon,
  orderAmount: number
): number => {
  if (orderAmount < coupon.minOrderAmount) {
    return 0;
  }

  if (coupon.discountType === 'fixed') {
    return Math.min(coupon.discountValue, orderAmount);
  }

  // percent discount
  return Math.floor((orderAmount * coupon.discountValue) / 100);
};
